/**
 * Gruppennamen aus einem Token in Plattformrollen übersetzen.
 *
 * Die sieben Rollennamen werden hier **nicht** noch einmal aufgeschrieben. Sie
 * stehen in `packages/contracts` als `ROLLE` und in
 * `infra/identity/rollen.yaml` — zwei Stellen sind schon eine zu viel, und
 * `infra/identity/konformitaet.py` vergleicht sie deshalb gegeneinander. Eine
 * dritte Kopie hier wäre die Stelle, an der es auseinanderläuft.
 */

import { ROLLE, type Rolle } from "@hwc/contracts";

const BEKANNT: ReadonlySet<string> = new Set<string>(ROLLE);

export interface Rollenbefund {
  readonly rollen: readonly Rolle[];
  /** Gruppen, die keiner Plattformrolle entsprechen. */
  readonly verworfen: readonly string[];
}

/**
 * Filtert Gruppennamen auf bekannte Rollen.
 *
 * Unbekannte Gruppen werden verworfen, aber **benannt**. Der Unterschied ist
 * der Kern: In `POC-IAM-001` sah eine Anwendung einen Benutzer als rechtlos,
 * weil der Gruppen-Mapper fehlte — ohne Fehlermeldung, ohne Protokolleintrag,
 * ohne irgendeinen Hinweis darauf, dass etwas fehlt. Wer verwirft, muss sagen
 * was; sonst ist "keine Rolle" nicht von "Rolle nicht angekommen" zu
 * unterscheiden.
 *
 * Doppelte Nennungen fallen weg. Nextcloud und Keycloak liefern dieselbe
 * Gruppe je nach Mapper-Einstellung mehrfach.
 */
export function rollenAusGruppen(gruppen: readonly string[]): Rollenbefund {
  const rollen: Rolle[] = [];
  const verworfen: string[] = [];
  for (const g of gruppen) {
    if (BEKANNT.has(g)) {
      if (!rollen.includes(g as Rolle)) rollen.push(g as Rolle);
    } else if (!verworfen.includes(g)) {
      verworfen.push(g);
    }
  }
  return { rollen, verworfen };
}

/**
 * Entfernt einen führenden Schrägstrich aus einem Gruppennamen.
 *
 * Keycloak liefert `/office` statt `office`, sobald der Gruppen-Mapper auf
 * `full.path: true` steht. Die Vorlage in `infra/keycloak/` setzt ihn auf
 * `false`, aber ein anderer Anbieter — oder eine von Hand geänderte
 * Einstellung — liefert die Pfadform. Ohne diese Normalisierung ist dann
 * **jede** Gruppe unbekannt, und der Benutzer steht rechtlos da.
 *
 * Verschachtelte Gruppen (`/kunde/office`) sind bewusst nicht abgebildet: Das
 * Rollenmodell aus `01` §1.6 kennt keine Hierarchie, und aus `/a/b` einfach
 * `b` zu machen hieße, eine Struktur stillschweigend einzuebnen, die es hier
 * nicht geben darf.
 */
export function ohnePfad(gruppe: string): string {
  return gruppe.startsWith("/") ? gruppe.slice(1) : gruppe;
}
